import React, { ReactElement, useState } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { PersonAddAlt1 as PersonAddIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import { gray40, gray80, gray100, white } from '../../../constants/colors';
import { referralMessages } from '../../../constants/messages';
import ReferralInviteDialog from '../dialog/ReferralInviteDialog';

/** チュートリアルの「次へ」ボタンと同じ色 */
const NAVY_BUTTON_COLOR = '#171b4b';
const NAVY_BUTTON_HOVER = '#0f1235';

interface ReferralInvitationCardProps {
  compact?: boolean;
  disabled?: boolean;
}

const DISABLED_TOOLTIP_TEXT = '運営スタッフは招待機能を利用できません';

/**
 * 招待リンク生成カード
 * ボタン押下 → ダイアログで招待リンクを表示
 */
const ReferralInvitationCard = ({
  compact = false,
  disabled = false,
}: ReferralInvitationCardProps): ReactElement => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const tooltipContent = (
    <Box sx={{ fontSize: '12px', lineHeight: '1.6' }}>
      ご紹介いただいた企業がGen Driveに登録すると、あなたに
      <strong>10,000円分</strong>
      、お相手に
      <strong>5,000円分</strong>
      のOCRクレジットをお贈りします。
    </Box>
  );

  // コンパクト表示（ヘッダー横）
  if (compact) {
    const compactButton = (
      <Button
        sx={styles.compactButton}
        onClick={(): void => setDialogOpen(true)}
        startIcon={<PersonAddIcon />}
        size="small"
        disabled={disabled}
      >
        {referralMessages.dialogTitle}
      </Button>
    );
    return (
      <>
        <Box sx={styles.compactContainer}>
          {disabled ? (
            <Tooltip title={DISABLED_TOOLTIP_TEXT} arrow placement="bottom">
              <span style={{ display: 'inline-flex' }}>{compactButton}</span>
            </Tooltip>
          ) : (
            compactButton
          )}
          <Tooltip
            title={tooltipContent}
            arrow
            placement="bottom"
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <HelpIcon sx={styles.compactHelpIcon} />
          </Tooltip>
        </Box>
        <ReferralInviteDialog open={dialogOpen} onClose={(): void => setDialogOpen(false)} />
      </>
    );
  }

  // 通常表示（カード形式）
  const createButton = (
    <Button
      sx={styles.createButton}
      onClick={(): void => setDialogOpen(true)}
      startIcon={<PersonAddIcon />}
      disabled={disabled}
    >
      {referralMessages.createLinkButton}
    </Button>
  );

  return (
    <>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{referralMessages.cardTitle}</Typography>
          <Tooltip
            title={tooltipContent}
            arrow
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <HelpIcon sx={styles.helpIcon} />
          </Tooltip>
        </Box>
        {disabled ? (
          <Tooltip title={DISABLED_TOOLTIP_TEXT} arrow placement="top">
            <span style={{ display: 'flex', width: '100%' }}>{createButton}</span>
          </Tooltip>
        ) : (
          createButton
        )}
      </Box>
      <ReferralInviteDialog open={dialogOpen} onClose={(): void => setDialogOpen(false)} />
    </>
  );
};

const styles = {
  // 通常表示（カード形式）
  container: {
    backgroundColor: white,
    borderRadius: '8px',
    padding: '16px',
    border: `1px solid ${gray40}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  title: {
    fontSize: '16px',
    fontWeight: '700',
    color: gray100,
    flex: 1,
  },
  helpIcon: {
    color: gray80,
    fontSize: '18px',
    cursor: 'help',
  },
  createButton: {
    backgroundColor: NAVY_BUTTON_COLOR,
    color: white,
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '6px',
    padding: '8px 16px',
    boxShadow: 'none',
    width: '100%',
    '&:hover': {
      backgroundColor: NAVY_BUTTON_HOVER,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: gray40,
      color: gray80,
    },
  },
  // コンパクト表示（ヘッダー横）
  compactContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    '@media screen and (max-width: 960px)': {
      display: 'none',
    },
  },
  compactHelpIcon: {
    color: gray80,
    fontSize: '16px',
    cursor: 'help',
  },
  compactButton: {
    backgroundColor: NAVY_BUTTON_COLOR,
    color: white,
    fontWeight: 700,
    fontSize: '13px',
    borderRadius: '6px',
    padding: '6px 12px',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: NAVY_BUTTON_HOVER,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: gray40,
      color: gray80,
    },
  },
};

export default ReferralInvitationCard;
